import React, { useEffect, useState } from "react";
import { WORKING_HOURS_REPORT } from "../../constants";
import axios from "axios";
import EmployeeWorkHoursReport from "./EmployeeWorkHourReport";
import dayjs from "dayjs";
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import FilterComponent from "./FilterPanel";
import FilterListIcon from "@mui/icons-material/FilterList";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const buttonStyle = {
  border: "1px solid #fff",
  borderRadius: "4px",
  margin: "5px",
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#097969",
  color: "#fff",
  transition: "background-color 0.3s, transform 0.2s",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
};

const hoverEffects = (e, isHovering) => {
  e.currentTarget.style.backgroundColor = isHovering ? "#0B9B73" : "#097969";
  e.currentTarget.style.transform = isHovering ? "scale(1.05)" : "scale(1)";
};

function AgentWorkingHourReportComponent({}) {
  const today = dayjs().startOf("day");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    startDate: today.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
    endDate: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
    agentName: "",
  });

  const [filterShow, setFilterShow] = useState(false);

  const empDetails = JSON.parse(localStorage.getItem("userData"));

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = filters;

      // Build query parameters dynamically
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (localStorage.getItem("empRole") === "100") {
        queryParams.append("agentNumber", empDetails.EmployeePhone);
      }

      const response = await axios.get(
        `${WORKING_HOURS_REPORT}?${queryParams.toString()}`
      );

      setData(response.data);
    } catch (error) {
      console.error("Error fetching data", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [filters]);

  const handleExport = () => {
    if (!data) return;
    const exportData = data.employeeStats.map((employee) => ({
      EmployeeName: employee.employeeName,
      TotalWeekdays: employee.totalWeekdays,
      WeekdayHours: employee.totalWeekdayHours,
      Saturdays: employee.saturdays,
      SaturdayHours: employee.totalSaturdayHours,
      TotalExpectedHours: employee.totalExpectedHours,
      TotalWorkedHours: employee.totalWorkedHours,
      TotalIncomingCall: employee.callDetails?.incoming?.count,
      TotalIncomingHours: employee.callDetails?.incoming?.hours,
      TotalOutgoingCall: employee.callDetails?.outgoing?.count,
      TotalOutGoingHours: employee.callDetails?.outgoing?.hours,
      WorkPercentage: `${employee.totalWorkedPercentage}%`,
    }));
    // Add grand total as the last row
    // exportData.push({
    //   EmployeeName: "Grand Total",
    //   TotalWeekdays: "-", // Not relevant for grand total
    //   WeekdayHours: "-",
    //   Saturdays: "-",
    //   SaturdayHours: "-",
    //   TotalExpectedHours: data.grandTotal.totalExpectedHours,
    //   TotalWorkedHours: data.grandTotal.totalWorkedHours,
    //   TotalIncomingCall: data.grandTotal?.incoming?.count,
    //   TotalIncomingHours: data.grandTotal?.incoming?.hours,
    //   TotalOutgoingCall: data.grandTotal?.outgoing?.count,
    //   TotalOutGoingHours: data.grandTotal?.outgoing?.hours,
    //   WorkPercentage: `${data.grandTotal.totalWorkedPercentage}%`,
    // });

    const worksheet = XLSX.utils.json_to_sheet(exportData); // Convert JSON data to sheet
    const workbook = XLSX.utils.book_new(); // Create a new workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1"); // Append the sheet

    // Write the workbook and download it
    XLSX.writeFile(workbook, `Workin_hour_Report.xlsx`);
  };

  // Conditional Rendering
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh", // Full viewport height
          backgroundColor: "#f9f9f9",
        }}
      >
        <Card
          sx={{
            padding: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            boxShadow: 3,
            maxWidth: 400,
            width: "90%", // Responsive width
          }}
        >
          <CircularProgress size={60} sx={{ color: "#097969" }} />
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
            Fetching data...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we prepare the report for you.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: 1,
          }}
        >
          <div>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", marginBottom: 2 }}
            >
              Employee Work Hours Report
            </Typography>
          </div>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1, // Space between items
            }}
          >
            {/* Filter Component */}
            {filterShow && (
              <Box sx={{ marginRight: 2 }}>
                <FilterComponent
                  filters={filters} // Pass current filters
                  setFilters={setFilters} // Pass the setFilters function
                  data={data} // Pass current data (for agent options)
                  setFilterShow={setFilterShow} // You can skip this if no modal is used
                />
              </Box>
            )}

            {/* Filter Icon */}
            <IconButton
              onClick={() => setFilterShow((prev) => !prev)}
              style={buttonStyle}
              onMouseEnter={(e) => hoverEffects(e, true)}
              onMouseLeave={(e) => hoverEffects(e, false)}
            >
              <FilterListIcon />
            </IconButton>

            {/* Export Icon */}
            <IconButton
              onClick={handleExport}
              style={{
                border: "1px solid #fff", // Change border color to white for contrast
                borderRadius: "4px",
                margin: "5px",
                width: "40px", // Increased width for a more comfortable click area
                height: "40px", // Increased height for a more comfortable click area
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#097969",
                color: "#fff",
                transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
                e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#097969"; // Original color
                e.currentTarget.style.transform = "scale(1)"; // Reset scale
              }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Report Component */}
        <EmployeeWorkHoursReport data={data} />
      </Box>
    </>
  );
}

export default AgentWorkingHourReportComponent;
